import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, Stack, Grid,
  CircularProgress, Alert, Tabs, Tab, TextField, InputAdornment, TablePagination,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { CloudUpload, GetApp, CloudDone, Construction, Terminal, Search, Inventory2 } from '@mui/icons-material';
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import OpacityIcon from '@mui/icons-material/Opacity';
import { CreateReleaseModal } from './CreateReleaseModal';
import { ReleaseNotesViewer } from './ReleaseNotesViewer';
import { DeleteBuildButton } from './DeleteBuildButton';

const API_BASE = 'https://console.dreamtec.io';
const TYPE_FILTERS = ['ALL', 'APK', 'PCG', 'AAB'];
const TYPE_STYLES = {
  APK: { bgcolor: 'rgba(47, 114, 124, 0.08)', color: '#2F727C' },
  PCG: { bgcolor: 'rgba(246, 185, 59, 0.12)', color: '#B7791F' },
  AAB: { bgcolor: 'rgba(59, 130, 246, 0.08)', color: '#1d4ed8' },
};

const headCell = { fontWeight: 800, color: '#94a3b8', fontSize: '0.75rem' };
const getType = (r) => (r.package_type || 'APK').toUpperCase();
const toTime = (iso) => (iso ? new Date(iso).getTime() : 0);

export const ReleaseManagement = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const [activeTab, setActiveTab] = useState('FUEL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // The API filters by customer_type (and defaults to FUEL if it isn't sent),
  // so fetch per tab. Without this the FOOD tab is always empty.
  const fetchReleases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/app-versions?customer_type=${activeTab}`, {
        headers: { accept: 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load build catalog');
      setReleases(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { fetchReleases(); }, [fetchReleases]);

  // Tab-level list, always newest upload first.
  // (Old code did b.uploaded_at - a.uploaded_at on ISO strings → NaN → no sorting.)
  const tabReleases = useMemo(
    () => [...releases].sort((a, b) => toTime(b.uploaded_at) - toTime(a.uploaded_at) || b.id - a.id),
    [releases]
  );

  // Type filter + search, applied on top
  const filteredReleases = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return tabReleases.filter((r) => {
      const matchesType = typeFilter === 'ALL' || getType(r) === typeFilter;
      const matchesSearch = !q
        || r.version_label?.toLowerCase().includes(q)
        || r.version_code?.toString().includes(q);
      return matchesType && matchesSearch;
    });
  }, [tabReleases, typeFilter, searchQuery]);

  // Count per type for the filter buttons
  const typeCounts = useMemo(() => {
    const counts = { ALL: tabReleases.length, APK: 0, PCG: 0, AAB: 0 };
    tabReleases.forEach((r) => { counts[getType(r)] = (counts[getType(r)] || 0) + 1; });
    return counts;
  }, [tabReleases]);

  // Summary cards use the whole tab, so they don't change while searching/filtering
  const latestProd = tabReleases.find((r) => r.environment?.toUpperCase() === 'PRODUCTION' && r.is_active);
  const latestTest = tabReleases.find((r) => r.environment?.toUpperCase() === 'TEST' && r.is_active);

  // Keep the page valid after deletes / filter changes
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredReleases.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [filteredReleases.length, rowsPerPage, page]);

  // Authenticated download of any type (APK/PCG/AAB) through the admin endpoint
  const handleDownload = async (rel) => {
    setDownloadingId(rel.id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/app-versions/${rel.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tms-${activeTab.toLowerCase()}-v${rel.version_label}.${getType(rel).toLowerCase()}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleted = (id) => setReleases((prev) => prev.filter((r) => r.id !== id));

  return (
    <Box sx={{ width: '100%', p: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1A3A3F', letterSpacing: -0.5 }}>
          App Releases
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search version..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ width: 220, bgcolor: 'white' }}
          />
          <Button variant="contained" startIcon={<CloudUpload />} onClick={() => setIsModalOpen(true)} color="primary">
            Create Release
          </Button>
        </Stack>
      </Stack>

      <Tabs
        value={activeTab}
        onChange={(e, v) => { setActiveTab(v); setPage(0); }}
        sx={{
          mb: 3,
          '& .MuiTab-root': { fontWeight: 800, textTransform: 'none', minHeight: 48, fontSize: '0.85rem' },
          '& .Mui-selected': { color: '#2F727C !important' },
          '& .MuiTabs-indicator': { backgroundColor: '#2F727C', height: 3 },
        }}
      >
        <Tab value="FUEL" icon={<OpacityIcon fontSize="small" />} iconPosition="start" label="FUEL" />
        <Tab value="FOOD" icon={<LunchDiningIcon fontSize="small" />} iconPosition="start" label="FOOD" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <SummaryCard icon={<CloudDone fontSize="medium" />} tint="rgba(47, 114, 124, 0.1)" color="#2F727C"
            label={`LATEST ${activeTab} PRODUCTION`} build={latestProd} />
        </Grid>
        <Grid item xs={12} md={6}>
          <SummaryCard icon={<Construction fontSize="medium" />} tint="rgba(246, 185, 59, 0.1)" color="#F6B93B"
            label={`LATEST ${activeTab} TEST`} build={latestTest} />
        </Grid>
      </Grid>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', ml: 1, letterSpacing: 1 }}>
          {activeTab} BUILD REPOSITORY
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={typeFilter}
          onChange={(_, v) => { if (v) { setTypeFilter(v); setPage(0); } }}
          sx={{ bgcolor: 'white', '& .MuiToggleButton-root': { fontWeight: 800, fontSize: '0.72rem', px: 1.5, py: 0.5 } }}
        >
          {TYPE_FILTERS.map((t) => (
            <ToggleButton key={t} value={t}>
              {t === 'ALL' ? 'All' : t} ({typeCounts[t] || 0})
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'auto', position: 'relative' }}>
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,0.6)', zIndex: 2 }}>
            <CircularProgress sx={{ color: '#2F727C' }} />
          </Box>
        )}
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ ...headCell, py: 2 }}>BUILD LABEL</TableCell>
              <TableCell sx={headCell}>VERSION CODE</TableCell>
              <TableCell sx={headCell}>TYPE</TableCell>
              <TableCell sx={headCell}>ENVIRONMENT</TableCell>
              <TableCell sx={{ ...headCell, minWidth: 260 }}>RELEASE NOTES</TableCell>
              <TableCell sx={headCell}>FILE SIZE</TableCell>
              <TableCell sx={headCell}>UPLOADED ↓</TableCell>
              <TableCell sx={headCell}>UPLOADED BY</TableCell>
              <TableCell align="right" sx={headCell}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredReleases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 10, color: '#94a3b8' }}>
                  {loading ? ' ' : `No ${activeTab}${typeFilter !== 'ALL' ? ` ${typeFilter}` : ''} builds found matching criteria.`}
                </TableCell>
              </TableRow>
            ) : (
              filteredReleases
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((rel) => {
                  const type = getType(rel);
                  return (
                    <TableRow key={rel.id} hover sx={{ verticalAlign: 'top', opacity: rel.is_active ? 1 : 0.55 }}>
                      <TableCell sx={{ py: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          {type === 'PCG'
                            ? <Inventory2 sx={{ fontSize: 18, color: '#B7791F' }} />
                            : <Terminal sx={{ fontSize: 18, color: '#2F727C' }} />}
                          <Typography variant="body2" sx={{ fontWeight: 900, color: '#1A3A3F' }}>
                            {rel.version_label}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#5d7e83' }}>
                          {rel.version_code}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ py: 2 }}>
                        <Chip label={type} size="small"
                          sx={{ fontWeight: 800, fontSize: '0.65rem', borderRadius: 1.5, border: '1px solid currentColor', ...TYPE_STYLES[type] }} />
                      </TableCell>

                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={rel.environment?.toUpperCase() || 'N/A'}
                          size="small"
                          sx={{
                            fontWeight: 800, fontSize: '0.65rem', borderRadius: 1.5, border: '1px solid currentColor',
                            bgcolor: rel.environment?.toUpperCase() === 'PRODUCTION' ? 'rgba(47, 114, 124, 0.08)' : 'rgba(246, 185, 59, 0.08)',
                            color: rel.environment?.toUpperCase() === 'PRODUCTION' ? '#2F727C' : '#B7791F',
                          }}
                        />
                      </TableCell>

                      <TableCell sx={{ py: 1.5, maxWidth: 340 }}>
                        <ReleaseNotesViewer
                          notes={rel.release_notes}
                          maxHeight={84}
                          expandable
                          title={`${activeTab} v${rel.version_label} (${rel.version_code}) release notes`}
                        />
                      </TableCell>

                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: '#5d7e83', fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {rel.file_size_bytes ? `${(rel.file_size_bytes / (1024 * 1024)).toFixed(2)} MB` : 'N/A'}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ py: 2 }}>
                        <Tooltip title={rel.uploaded_at ? new Date(rel.uploaded_at).toLocaleString() : ''}>
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            {rel.uploaded_at ? new Date(rel.uploaded_at).toLocaleDateString() : '—'}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="caption" sx={{ color: '#1A3A3F', fontWeight: 800, fontSize: '0.8rem' }}>
                          {rel.uploaded_by || 'Unknown'}
                        </Typography>
                      </TableCell>

                      <TableCell align="right" sx={{ py: 1.5 }}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title={`Download ${type}`}>
                            <span>
                              <IconButton size="small" color="primary" onClick={() => handleDownload(rel)} disabled={downloadingId === rel.id}>
                                {downloadingId === rel.id ? <CircularProgress size={16} /> : <GetApp fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <DeleteBuildButton build={rel} onDeleted={handleDeleted} />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredReleases.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>

      <CreateReleaseModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => { setPage(0); fetchReleases(); }}
      />
    </Box>
  );
};

const SummaryCard = ({ icon, tint, color, label, build }) => (
  <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{ p: 1.5, bgcolor: tint, borderRadius: 2, color }}>{icon}</Box>
      <Box>
        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, letterSpacing: 1 }}>{label}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 900, color: '#1A3A3F' }}>
            {build ? `v${build.version_label}` : 'N/A'}
          </Typography>
          {build && <Chip size="small" label={getType(build)} sx={{ fontWeight: 800, fontSize: '0.6rem', height: 18, ...TYPE_STYLES[getType(build)] }} />}
        </Stack>
      </Box>
    </Stack>
  </Paper>
);